import { useCallback, useRef, useState } from 'react';
import api from '../../utils/api';
import { normaliseTag } from '../../utils/specimenCode';

// What a label check can say that keeps Save off: another record carries the
// code (a deleted one still holds it), or a pre-printed label is paired.
export const BLOCKING_TAG = ['inUse', 'reserved', 'paired'];

/**
 * The check of a label typed or scanned for a specimen.
 *
 * A duplicate tag in the field means two specimens that can never be told
 * apart afterwards, so it is checked as it is entered, not at sending.
 *
 * Two questions, asked together: does a record already carry this code
 * (/lookup), and was the label printed ahead of time in a batch
 * (/qr-pool/{code}, routes/qr_pool.py)? A pre-printed label that is still
 * free is the expected case; one already paired is as bad as a code in use;
 * a code from no batch still works, but is worth a second look at the label.
 * Offline, neither answers and the tag is taken as typed.
 *
 * Every answer names the code it is about, and one that arrives after a newer
 * check or an edit of the label is dropped: an older answer must not clear
 * the block on a label in use, or block a free one.
 *
 * `canAsk` says whether a request may go now: one under a token that has run
 * out is a 401, and a 401 signs the person out of the app mid-edit.
 */
export function useTagCheck(initial = { status: 'idle' }, canAsk = () => true) {
  const [tagState, setState] = useState(initial);
  const request = useRef(0);
  const asking = useRef(canAsk);
  asking.current = canAsk;

  const checkTag = useCallback(async (code) => {
    request.current += 1;
    const mine = request.current;
    const value = normaliseTag(code);
    if (!value || !asking.current()) return setState({ status: 'idle' });
    setState({ status: 'checking', code: value });
    const [record, label] = await Promise.allSettled([
      api.get('/lookup', { params: { code: value } }),
      api.get(`/qr-pool/${encodeURIComponent(value)}`),
    ]);
    if (mine !== request.current) return undefined;
    const answer = status => setState({ status, code: value });
    if (record.status === 'fulfilled') {
      const r = record.value;
      // A specimen label not yet tied to a collection is MEANT to be used as
      // this collection's tag: the server links the two on save.
      const freeSpecimen = r.data?.type === 'specimen' && !r.data.sample_id && !r.data.external_id
        && ['reserved', 'active'].includes(r.data.status);
      if (freeSpecimen) return answer('specimenLabel');
      const hit = r.data && (r.data.type || r.data.record_id || r.data.sample_id);
      if (hit) return answer('inUse');
    } else if (record.reason?.response?.status !== 404) {
      return setState({ status: 'idle' });
    }
    // A 404 from /lookup is the good outcome: nothing already owns this tag.
    const pool = label.status === 'fulfilled' ? label.value.data : null;
    if (!pool) return answer('free');
    if (pool.preprinted && pool.claimed) return answer('paired');
    if (pool.preprinted) return answer('preprinted');
    return answer('notPrinted');
  }, []);

  /** The label was edited: an answer about the old one no longer applies. */
  const resetTag = useCallback(() => {
    request.current += 1;
    setState(s => (s.status === 'idle' ? s : { status: 'idle' }));
  }, []);

  return { tagState, checkTag, resetTag };
}

/** Whether this answer, about the label as it will be sent, keeps Save off. */
export const tagBlocks = (tagState, label) => {
  const tag = normaliseTag(label);
  return !!tag && BLOCKING_TAG.includes(tagState.status) && tagState.code === tag;
};
